import { Component, Input, OnChanges } from "@angular/core";
import { AssessmentDto, FormType } from "@/_models";
import { TableColumn } from "@/components/shared/table-header/table-header.component";
import { FormPreviewModalComponent } from "@/components/assessment/form-preview-modal/form-preview-modal.component";
import { catchError, of, tap } from "rxjs";
import { filter, switchMap } from "rxjs/operators";
import {
    CreateAssessmentDto,
    CreateMode,
} from "@/_models/assessment/CreateAssessmentDto";
import { AccountService } from "@/_services/account.service";
import { AssessmentService } from "@/_services";
import { Router } from "@angular/router";
import { ToastService } from "@/_services/ToastService";
import { AppDialogService } from "@/_services/app-dialog.service";
@Component({
    selector: "app-template-card",
    templateUrl: "./template-card.component.html",
    styleUrl: "./template-card.component.scss",
})
export class TemplateCardComponent implements OnChanges {
    @Input() template: AssessmentDto;
    @Input() columns: TableColumn[] = [];
    @Input() formTypeList: FormType[];
    @Input() gridTemplate = "";

    readonly creationModes = CreateMode;

    // Precomputed for the Industry column (templates can't bind to methods).
    // "all" is an authoring-only tag, never shown to users.
    industriesLabel = "—";

    ngOnChanges(): void {
        const industries = (this.template?.industries ?? []).filter(
            (industry) => industry !== "all",
        );
        this.industriesLabel = industries.length
            ? industries.map((industry) => this.titleCase(industry)).join(", ")
            : "—";
    }

    private titleCase(value: string): string {
        return value
            .replace(/[_-]+/g, " ")
            .split(" ")
            .filter(Boolean)
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    }

    constructor(
        private appDialogService: AppDialogService,
        private accountService: AccountService,
        private assessmentService: AssessmentService,
        private router: Router,
        private toastrService: ToastService,
    ) {}

    openPreview() {
        this.appDialogService.open<void, AssessmentDto>(
            FormPreviewModalComponent,
            {
                title: this.template.title,
                size: "md",
                data: this.template,
            },
        );
    }

    edit() {
        this.router.navigate(["./assessments", this.template._id, "edit"]);
    }

    clone() {
        this.accountService
            .checkFormFeature(this.template.formTypeSettings.requiresFeature)
            .pipe(
                filter((result) => result),
                switchMap(() =>
                    this.assessmentService.create({
                        method: this.template.account
                            ? this.creationModes.Copy
                            : this.creationModes.Default,
                        copyFrom: this.template.account
                            ? this.template._id
                            : this.template.title,
                    } as CreateAssessmentDto),
                ),
                tap((assessment: AssessmentDto) =>
                    this.router.navigate([
                        "./assessments",
                        assessment._id,
                        "edit",
                    ]),
                ),
                catchError((error) => {
                    this.toastrService.error(error, "Error creating form");
                    return of(error);
                }),
            )
            .subscribe();
    }
}
